import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Star, Award, ShieldCheck, Users, BarChart3, Zap, BadgeCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { tradingPlatformLink } from '../../utils/tradingPlatform';
import LivePhoneMockup from './LivePhoneMockup';
import LiveUserCounter from './LiveUserCounter';

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = (n) => `${API}/api/media/generated/${n}`;

const SectionHeader = ({ eyebrow, title, sub, align = 'center' }) => {
 const { isRTL } = useLanguage();
 return (
 <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : isRTL ? 'text-right ms-0 me-auto' : ''}`}>
 {eyebrow && <p className="varo-eyebrow mb-4">{eyebrow}</p>}
 <h2 className={`varo-display varo-display-md ${isRTL ? 'leading-[1.18]' : ''}`}>{title}</h2>
 {sub && <p className="mt-5 text-ink-600 text-[16px] leading-relaxed">{sub}</p>}
 </div>
 );
};

/* 1. THREE FEATURED CARDS (Vantage exact style) */
export const FeaturedCards = () => {
 const { isRTL } = useLanguage();
 const cards = [
 { img: IMG('feature_drone_gift.png'), tag: isRTL ? 'مكافآت' : 'Rewards', title: isRTL ? 'بونص ترحيبي 50% على أوّل إيداع' : '50% welcome bonus on your first deposit', cta: isRTL ? 'استلم البونص' : 'Claim now', link: '/register' },
 { img: IMG('feature_coins_3d.png'), tag: isRTL ? 'الأسواق' : 'Markets', title: isRTL ? 'أكثر من 2,000 أداة عالمية للتداول' : '2,000+ global instruments to trade', cta: isRTL ? 'استكشف' : 'Explore', link: '/markets' },
 { img: IMG('feature_laptop_chart.png'), tag: isRTL ? 'المنصة' : 'Platform', title: isRTL ? 'تداول بذكاء على VAROTRADES Web Trading' : 'Trade smarter on VAROTRADES Web Trading', cta: isRTL ? 'افتحها' : 'Launch app', link: 'external-platform' },
 ];
 return (
 <section className="relative py-20 bg-gradient-to-br from-[#FFFAF3] via-[#FAF7F2] to-[#FFF3E8]">
 <div className="max-w-[1240px] mx-auto px-6">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
 {cards.map((c, i) => (
 <motion.div key={i}
 initial={{ opacity: 0, y: 24 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true, margin: '-50px' }}
 transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}>
 {c.link === 'external-platform' ? (
  <a {...tradingPlatformLink()} className="group varo-card varo-card-hover overflow-hidden block" data-testid={`featured-card-${i}`}>
   <div className="aspect-[16/10] bg-flame-50 overflow-hidden">
    <img src={c.img} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
   </div>
   <div className="p-6">
    <span className="inline-block text-[10px] uppercase tracking-[0.18em] font-bold text-flame-600 mb-2">{c.tag}</span>
    <h3 className="text-[18px] font-bold tracking-tight text-ink-900 mb-3 leading-snug">{c.title}</h3>
    <span className="inline-flex items-center gap-1.5 text-flame-600 text-sm font-bold group-hover:gap-2.5 transition-all">
     {c.cta} <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} />
    </span>
   </div>
  </a>
 ) : (
 <Link to={c.link} className="group varo-card varo-card-hover overflow-hidden block" data-testid={`featured-card-${i}`}>
 <div className="aspect-[16/10] bg-flame-50 overflow-hidden">
 <img src={c.img} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
 </div>
 <div className="p-6">
 <span className="inline-block text-[10px] uppercase tracking-[0.18em] font-bold text-flame-600 mb-2">{c.tag}</span>
 <h3 className="text-[18px] font-bold tracking-tight text-ink-900 mb-3 leading-snug">{c.title}</h3>
 <span className="inline-flex items-center gap-1.5 text-flame-600 text-sm font-bold group-hover:gap-2.5 transition-all">
 {c.cta} <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} />
 </span>
 </div>
 </Link>
 )}
 </motion.div>
 ))}
 </div>
 </div>
 </section>
 );
};

/* 2. SPREADS COMPARISON TABLE */
export const SpreadsCompare = () => {
 const { isRTL } = useLanguage();
 const rows = [
 { sym: 'EUR/USD', v: '0.0', m: '1.2' },
 { sym: 'GBP/USD', v: '0.3', m: '1.4' },
 { sym: 'XAU/USD', v: '0.18', m: '0.45' },
 { sym: 'BTC/USD', v: '0.5 bps', m: '4.5 bps' },
 { sym: 'NAS100', v: '0.6', m: '2.0' },
 { sym: 'WTI', v: '0.04', m: '0.12' },
 ];
 return (
 <section className="relative py-20 bg-gradient-to-br from-white via-[#FFFDF8] to-[#FAF7F2]">
 <div className="max-w-[1240px] mx-auto px-6">
 <SectionHeader
 eyebrow={isRTL ? 'فروقات الأسعار التنافسية' : 'Our Competitive Spreads'}
 title={isRTL ? 'الأكثر تنافسية في الصناعة.' : "Industry's tightest spreads."}
 />
 <div className="mt-12 max-w-3xl mx-auto rounded-3xl border border-[rgba(20,15,10,0.08)] overflow-hidden bg-white shadow-[0_18px_50px_rgba(20,15,10,0.06)]">
 <div className="grid grid-cols-3 px-8 py-4 bg-flame-50 border-b border-[rgba(20,15,10,0.06)]">
 <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-600">{isRTL ? 'الأداة' : 'Instrument'}</span>
 <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-flame-700 text-center">VAROTRADES</span>
 <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink-500 text-end">{isRTL ? 'متوسط السوق' : 'Market Average'}</span>
 </div>
 {rows.map((r, i) => (
 <div key={i} className="grid grid-cols-3 px-8 py-4 items-center border-b border-[rgba(20,15,10,0.04)] last:border-0 hover:bg-flame-50/40 transition-colors">
 <span className="text-ink-900 font-semibold tracking-tight font-mono text-[14px]">{r.sym}</span>
 <span className="text-flame-700 font-bold font-mono text-[15px] tabular-nums text-center inline-flex items-center justify-center gap-2">
 <Star className="w-3 h-3 fill-flame-500 text-flame-500" />{r.v}
 </span>
 <span className="text-ink-500 font-mono text-[14px] tabular-nums text-end">{r.m}</span>
 </div>
 ))}
 </div>
 </div>
 </section>
 );
};

/* 3. STATS w/ ISOMETRIC ILLUSTRATIONS */
const StatTile = ({ img, k, l }) => {
 const ref = useRef(null);
 const inView = useInView(ref, { once: true, margin: '-50px' });
 const [n, setN] = useState(0);
 const num = typeof k === 'number' ? k : parseFloat(String(k).replace(/[^\d.]/g, '')) || 0;
 useEffect(() => {
 if (!inView) return;
 let cur = 0;
 const step = num / 60;
 const timer = setInterval(() => {
 cur += step;
 if (cur >= num) { setN(num); clearInterval(timer); } else setN(cur);
 }, 18);
 return () => clearInterval(timer);
 }, [inView, num]);
 return (
 <div ref={ref} className="text-center">
 <div className="w-32 h-32 mx-auto mb-5 rounded-3xl overflow-hidden bg-flame-50">
 <img src={img} alt="" className="w-full h-full object-cover" />
 </div>
 <p className="text-4xl lg:text-5xl font-black tracking-[-0.04em] text-ink-900 font-mono tabular-nums">
 {String(k).startsWith('$') ? '$' : ''}{n < num ? Math.floor(n).toLocaleString() : k.toString().replace(/[$]/g, '')}
 {!String(k).match(/^\d/) && !String(k).startsWith('$') ? k : ''}
 </p>
 <p className="text-ink-600 text-sm mt-2 font-medium">{l}</p>
 </div>
 );
};

export const AwardWinning = () => {
 const { isRTL } = useLanguage();
 return (
 <section className="relative py-20 bg-gradient-to-br from-[#FFFAF3] via-[#FAF7F2] to-[#FFF3E8]">
 <div className="max-w-[1240px] mx-auto px-6">
 <SectionHeader
 eyebrow={isRTL ? 'وسيط حائز على جوائز' : 'Award-winning broker'}
 title={isRTL ? 'VAROTRADES — الأرقام تتكلم.' : 'VAROTRADES — the numbers speak.'}
 />
 <div className="mt-14 grid grid-cols-1 lg:grid-cols-4 gap-8">
 {/* Live users tile (special — shows live counter) */}
 <div className="text-center lg:col-span-1">
 <div className="w-32 h-32 mx-auto mb-5 rounded-3xl overflow-hidden bg-flame-50">
 <img src={IMG('stat_users.png')} alt="" className="w-full h-full object-cover" />
 </div>
 <LiveUserCounter className="justify-center" size="lg" />
 <p className="text-ink-600 text-sm mt-2 font-medium">{isRTL ? 'مستخدم مسجّل (مباشر)' : 'Registered users (live)'}</p>
 </div>
 <StatTile img={IMG('feature_coins_3d.png')} k="2,000+" l={isRTL ? 'منتج للتداول' : 'Tradable products'} />
 <StatTile img={IMG('stat_spreads.png')} k="0.0 pips" l={isRTL ? 'فروقات تبدأ من' : 'Spreads from'} />
 <StatTile img={IMG('stat_secure.png')} k="$0" l={isRTL ? 'رسوم إيداع' : 'Deposit fees*'} />
 </div>
 </div>
 </section>
 );
};

/* 4. TRUST LOGOS GRID */
export const TrustEarned = () => {
 const { isRTL } = useLanguage();
 const logos = ['Trustpilot', 'CNBC', 'Reuters', 'Financial Times', 'Bloomberg', 'Yahoo', 'AP', 'CNN', 'Forbes', 'Business Insider', 'WikiFX', 'ForexBrokers'];
 return (
 <section className="relative py-20 bg-gradient-to-br from-white via-[#FFFDF8] to-[#FAF7F2]">
 <div className="max-w-[1240px] mx-auto px-6">
 <SectionHeader
 eyebrow={isRTL ? 'الثقة التي اكتسبناها' : 'Trust we have earned'}
 title={isRTL ? 'موثوق به من أكبر العلامات الإعلامية والمالية.' : 'Trusted by leading media & finance brands.'}
 />
 <div className="mt-12 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-px bg-[rgba(20,15,10,0.06)] rounded-3xl overflow-hidden">
 {logos.map((l, i) => (
 <div key={i} className="bg-white py-7 px-4 flex items-center justify-center group hover:bg-flame-50 transition-all">
 <span className="text-ink-400 font-bold text-[15px] tracking-tight group-hover:text-flame-600 transition-colors">{l}</span>
 </div>
 ))}
 </div>
 <p className="text-center text-ink-500 text-xs mt-4">{isRTL ? '*المنشورات أعلاه قد ذكرت VAROTRADES في تغطياتها' : '*Outlets above have featured VAROTRADES in their coverage'}</p>
 </div>
 </section>
 );
};

/* 5. TRADE ANYTIME ANYWHERE — split with phone + person */
export const TradeAnywhere = () => {
 const { isRTL } = useLanguage();
 return (
 <section className="relative py-20 bg-gradient-to-br from-[#FFFAF3] via-[#FAF7F2] to-[#FFF3E8]">
 <div className="max-w-[1240px] mx-auto px-6">
 <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isRTL ? 'lg:rtl' : ''}`}>
 <div className="order-2 lg:order-1">
 <p className="varo-eyebrow mb-4">{isRTL ? 'تداول في أي وقت ومكان' : 'Trade anytime, anywhere'}</p>
 <h2 className="varo-display varo-display-md text-ink-900">
 {isRTL ? <>السوق <span className="varo-text-flame">على شاشتك</span> مباشرةً.</> : <>The market on <span className="varo-text-flame">your screen</span>, instantly.</>}
 </h2>
 <p className="mt-6 text-ink-600 text-[16px] leading-relaxed max-w-md">
 {isRTL ? 'منصّة ويب احترافية تعمل بدون تنزيل — من المتصفح مباشرة على الكمبيوتر، التابلت، أو الجوال. 1,000+ أداة CFD، تنفيذ < 30ms، رسوم بيانية متقدّمة، وأدوات إدارة المخاطر الكاملة.' : 'A pro web platform with zero install — runs straight from your browser on desktop, tablet, or mobile. 1,000+ CFDs, sub-30ms execution, advanced charts, and full risk-management tools.'}
 </p>
 <div className={`mt-8 flex flex-wrap gap-3 ${isRTL ? 'justify-end flex-row-reverse' : ''}`}>
 <a {...tradingPlatformLink()} className="varo-btn-primary inline-flex items-center gap-2 px-6 h-12 rounded-2xl bg-flame-500 hover:bg-flame-600 text-white font-bold text-[14px] transition-colors" data-testid="trade-anywhere-launch-btn">
 <Zap className="w-4 h-4 fill-white" />
 {isRTL ? 'افتح المنصّة الآن' : 'Launch web platform'}
 </a>
 <Link to="/register?type=demo" className="inline-flex items-center gap-2 px-6 h-12 rounded-2xl bg-white border border-[rgba(20,15,10,0.10)] hover:border-flame-300 hover:text-flame-600 text-ink-700 font-bold text-[14px] transition-all">
 {isRTL ? 'حساب تجريبي $50K' : 'Free $50K demo'} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
 </Link>
 </div>
 <div className={`mt-6 flex items-center gap-5 flex-wrap text-[12.5px] text-ink-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
 <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-flame-500" />{isRTL ? 'بدون تنزيل' : 'No install'}</span>
 <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-flame-500" />{isRTL ? 'يعمل على أي جهاز' : 'Any device'}</span>
 <span className="inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-flame-500" />{isRTL ? 'مزامنة فورية' : 'Instant sync'}</span>
 </div>
 </div>

 <div className="order-1 lg:order-2 relative">
 <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-ink-100 shadow-[0_30px_80px_rgba(20,15,10,0.18)]">
 <img src={IMG('person_laptop.png')} alt="" className="absolute inset-0 w-full h-full object-cover" />
 </div>
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ duration: 0.7, delay: 0.3 }}
 className="absolute -bottom-8 -start-6 lg:start-auto lg:-end-8 bg-white rounded-2xl p-4 shadow-[0_18px_50px_rgba(20,15,10,0.15)] border border-flame-100 max-w-[200px]"
 >
 <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
 <div className="w-9 h-9 rounded-full bg-flame-500 flex items-center justify-center"><BadgeCheck className="w-4 h-4 text-white" /></div>
 <div className={isRTL ? 'text-right' : ''}>
 <p className="text-ink-900 font-bold text-[12.5px] leading-tight">24/7 {isRTL ? 'دعم' : 'Support'}</p>
 <div className="flex items-center gap-0.5 mt-0.5">
 {[0,1,2,3,4].map(i => <Star key={i} className="w-2.5 h-2.5 fill-flame-500 text-flame-500" />)}
 </div>
 </div>
 </div>
 </motion.div>
 </div>
 </div>
 </div>
 </section>
 );
};

/* 6. TRADING EXCELLENCE w/ 3D coins */
export const TradingExcellence = () => {
 const { isRTL } = useLanguage();
 const cats = [
 { label: isRTL ? 'العملات الرقمية' : 'Crypto', count: '300+' },
 { label: isRTL ? 'الفوركس' : 'Forex', count: '60+' },
 { label: isRTL ? 'المعادن' : 'Metals', count: '15+' },
 { label: isRTL ? 'الطاقة' : 'Energy', count: '8+' },
 { label: isRTL ? 'المؤشرات' : 'Indices', count: '40+' },
 { label: isRTL ? 'السلع' : 'Commodities', count: '25+' },
 ];
 return (
 <section className="relative py-20 bg-gradient-to-br from-white via-[#FFFDF8] to-[#FAF7F2]">
 <div className="max-w-[1240px] mx-auto px-6">
 <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isRTL ? 'lg:rtl' : ''}`}>
 <div>
 <SectionHeader align="start"
 eyebrow={isRTL ? 'تميّز التداول' : 'Trading excellence'}
 title={isRTL ? 'تداول مع وسيط CFD رائد.' : 'Trade with a leading CFD broker.'}
 sub={isRTL ? 'تشكيلة واسعة من الأدوات بفروقات تنافسية وتنفيذ مؤسّسيّ.' : 'A vast range of instruments with razor-tight spreads and institutional execution.'}
 />
 <div className="mt-8 grid grid-cols-2 gap-3">
 {cats.map((c, i) => (
 <div key={i} className="flex items-center justify-between bg-flame-50/60 rounded-2xl p-4 border border-flame-100">
 <span className="text-ink-800 text-sm font-medium">{c.label}</span>
 <span className="text-flame-600 text-sm font-bold font-mono">{c.count}</span>
 </div>
 ))}
 </div>
 </div>
 <div className="relative">
 <img src={IMG('feature_coins_3d.png')} alt="" className="w-full rounded-3xl" />
 </div>
 </div>
 </div>
 </section>
 );
};

/* 7. SUPPORT & RESOURCES */
export const SupportResources = () => {
 const { isRTL } = useLanguage();
 const cards = [
 { img: IMG('support_24_7.png'), title: isRTL ? 'دعم 24/7' : '24/7 Customer Support', sub: isRTL ? 'فريقنا في خدمتك دائماً' : 'Our team is always here', cta: isRTL ? 'ابدأ محادثة' : 'Start a chat', link: '/learn' },
 { img: IMG('support_help.png'), title: isRTL ? 'مركز المساعدة' : 'Help Center', sub: isRTL ? 'إجابات لكل أسئلتك' : 'Answers to all questions', cta: isRTL ? 'استكشف' : 'Explore', link: '/learn' },
 { img: IMG('support_book.png'), title: isRTL ? 'الأكاديمية' : 'Learn', sub: isRTL ? 'دورات وفيديوهات' : 'Courses & videos', cta: isRTL ? 'ابدأ التعلّم' : 'Start learning', link: '/learn' },
 ];
 return (
 <section className="relative py-20 bg-gradient-to-br from-[#FFFAF3] via-[#FAF7F2] to-[#FFF3E8]">
 <div className="max-w-[1240px] mx-auto px-6">
 <SectionHeader
 eyebrow={isRTL ? 'الدعم والموارد' : 'Support & Resources'}
 title={isRTL ? 'نحن معك في كل خطوة.' : "We're with you every step."}
 />
 <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
 {cards.map((c, i) => (
 <Link key={i} to={c.link} className="varo-card varo-card-hover p-7 group block text-center">
 <img src={c.img} alt="" className="w-24 h-24 mx-auto mb-5 rounded-2xl object-cover" />
 <h3 className="text-[18px] font-bold tracking-tight text-ink-900 mb-2">{c.title}</h3>
 <p className="text-ink-600 text-sm mb-4">{c.sub}</p>
 <span className="inline-flex items-center gap-1.5 text-flame-600 text-sm font-bold group-hover:gap-2.5 transition-all">
 {c.cta} <ArrowRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} />
 </span>
 </Link>
 ))}
 </div>
 </div>
 </section>
 );
};

/* 8. START NOW — Light Vantage CTA */
export const StartNow = () => {
 const { isRTL } = useLanguage();
 return (
 <section className="relative py-24 overflow-hidden bg-gradient-to-br from-[#FFF6EC] via-[#FAF7F2] to-white text-ink-900 border-t border-flame-100">
 <div className="absolute -top-40 -end-40 w-[40rem] h-[40rem] bg-flame-300/40 blur-[140px] rounded-full pointer-events-none" />
 <div className="absolute -bottom-40 -start-40 w-[36rem] h-[36rem] bg-flame-200/50 blur-[140px] rounded-full pointer-events-none" />
 <div className="relative max-w-[1240px] mx-auto px-6 text-center">
 <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-flame-600 mb-5">{isRTL ? 'انطلق الآن' : 'Start now'}</p>
 <h2 className="varo-display varo-display-lg text-ink-900">
 {isRTL ? <>ابدأ تداولك <span className="varo-text-flame">اليوم.</span></> : <>Start your trading <span className="varo-text-flame">today.</span></>}
 </h2>
 <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-3xl mx-auto">
 {[
 { k: '5M+', l: isRTL ? 'مستخدم' : 'Users' },
 { k: '2,000+', l: isRTL ? 'منتج' : 'Products' },
 { k: '0.0', l: isRTL ? 'فروقات' : 'Spreads' },
 { k: '$0', l: isRTL ? 'رسوم إيداع' : 'Deposit fees' },
 ].map((s, i) => (
 <div key={i}>
 <p className="text-3xl lg:text-4xl font-black tracking-[-0.04em] text-ink-900 font-mono tabular-nums">{s.k}</p>
 <p className="text-ink-600 text-xs mt-1.5 uppercase tracking-wider font-semibold">{s.l}</p>
 </div>
 ))}
 </div>
 <Link to="/register" className="mt-10 varo-btn-primary h-14 px-8 text-[16px] inline-flex" data-testid="start-now-cta">
 {isRTL ? 'افتح حسابك الآن' : 'Start now'}
 <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
 </Link>
 </div>
 </section>
 );
};

/* Phone showcase tile (live animated mockup — no background image) */
export const PhoneShowcase = () => {
 const { isRTL } = useLanguage();
 return (
 <section className="relative py-20 overflow-hidden">
 <div className="max-w-[1240px] mx-auto px-6">
 <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isRTL ? 'lg:rtl' : ''}`}>
 <div className="relative flex justify-center">
 <motion.div
 initial={{ opacity: 0, y: 30, rotate: isRTL ? 6 : -6 }}
 whileInView={{ opacity: 1, y: 0, rotate: 0 }}
 viewport={{ once: true }}
 transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
 >
 <LivePhoneMockup rotate={isRTL ? 4 : -4} />
 </motion.div>
 <div className="absolute -bottom-4 inset-x-0 h-12 bg-gradient-to-t from-flame-200/20 to-transparent blur-2xl pointer-events-none" />
 </div>
 <div>
 <SectionHeader align="start"
 eyebrow={isRTL ? 'تطبيق VAROTRADES' : 'VAROTRADES App'}
 title={isRTL ? 'شارت احترافي. مباشر في يدك.' : 'Pro charts. Right in your hand.'}
 sub={isRTL ? 'كل أدوات التحليل والتنفيذ التي تحتاجها — مصمّمة بدقّة للموبايل.' : 'All the analysis and execution tools you need — built precision-grade for mobile.'}
 />
 <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
 {[
 { icon: BarChart3, t: isRTL ? 'شارت بـ100+ مؤشّر' : '100+ indicators' },
 { icon: Zap, t: isRTL ? 'تنفيذ ميلي ثانية' : 'Millisecond exec' },
 { icon: ShieldCheck, t: isRTL ? 'مصادقة بيومترية' : 'Biometric login' },
 { icon: Users, t: isRTL ? 'دعم متعدّد اللغات' : 'Multi-language' },
 ].map((f, i) => (
 <div key={i} className={`flex items-center gap-3 bg-flame-50/60 rounded-2xl p-3.5 border border-flame-100 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
 <f.icon className="w-4 h-4 text-flame-600 shrink-0" />
 <span className="text-ink-700 text-[12.5px] font-medium">{f.t}</span>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </section>
 );
};

/* Backwards-compat aliases (in case any page imports old names) */
export const TrustStrip = TrustEarned;
export const StatsCounter = AwardWinning;
export const BentoMarkets = TradingExcellence;
export const WhyVarotrade = SupportResources;
export const SpreadsTable = SpreadsCompare;
export const MobileSection = PhoneShowcase;
export const PromotionCards = FeaturedCards;
export const FinalCTA = StartNow;
